'use client';

import { keepPreviousData } from '@tanstack/react-query';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { selectAtom } from 'jotai/utils';

import { SpecSnapshot, getSpecSnapshotApi } from '@flipster/apis/v2/snapshot';
import { marketsBunja } from '@flipster/r230522/original/market';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';

import { resolvePositionBracket } from '../position-bracket/utils';
import { userRegulationBunja } from '../regulation';
import { snapshotSpecResolver, stripVaraPrefix, type InstrumentV2ForApiInstanceType, type SnapshotSpec } from './utils';

export const rawSnapshotSpecQueryAtom = atomWithQuery((get) => ({
  queryKey: ['getSnapshotSpec', 'custom'],
  queryFn: async () => {
    const res = await getSpecSnapshotApi({ showAll: true });
    return snapshotSpecResolver(res.data);
  },
  initialData() {
    const queryClient = get(queryClientAtom);
    const hydratedData = queryClient.getQueryData<SpecSnapshot>(['fetchSpecSnapshotApi']);
    if (hydratedData) return snapshotSpecResolver(hydratedData);
  },
  placeholderData: keepPreviousData,
  retry: 1,
  retryDelay: 1000 * 3,
}));

export const rawSnapshotSpecAtom = atom<SnapshotSpec | undefined>((get) => get(rawSnapshotSpecQueryAtom).data);
export const rawInstrumentsKeyBySymbolAtom = atom((get) => get(rawSnapshotSpecAtom)?.instrumentsKeyBySymbol);
export const rawCurrenciesKeyByCurrencyAtom = atom((get) => get(rawSnapshotSpecAtom)?.currenciesKeyByCurrency);

export const snapshotSpecBunja = bunja(() => {
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { shouldFilterToVaraSymbolsAtom } = bunja.use(userRegulationBunja);

  // notional 상한은 private/pswaps에서 온다. price/tick마다 갱신되는 여타 필드로 인한 재계산을 막기 위해
  // notional 값만 선택해 변경 시에만 emit한다.
  const lowFreqUpdatedPswapFamily = atomFamily((symbol: string) =>
    selectAtom(
      pswapsAtom,
      (pswaps) => {
        const pswap = pswaps?.[symbol];
        if (!pswap) return;
        const { notionalMaxOrderAmount, notionalMaxPosition } = pswap;
        return { notionalMaxOrderAmount, notionalMaxPosition };
      },
      (a, b) =>
        a?.notionalMaxOrderAmount === b?.notionalMaxOrderAmount && a?.notionalMaxPosition === b?.notionalMaxPosition,
    ),
  );

  // 활성 positionBracket id는 market/pswaps(Market)로 실시간 전달된다(백엔드 스펙). marketsAtom은 매 틱
  // 갱신되므로 positionBracket만 선택해 dedup — 변경(off-hours 스위칭) 시에만 snapshotSpec을 재계산한다.
  const marketPositionBracketFamily = atomFamily((symbol: string) =>
    selectAtom(marketsAtom, (markets) => markets?.[symbol]?.positionBracket),
  );

  const snapshotSpecAtom = atom<SnapshotSpec | undefined>((get) => {
    const rawSnapshotSpecData = get(rawSnapshotSpecAtom);
    const shouldFilterToVaraSymbols = get(shouldFilterToVaraSymbolsAtom);

    if (!rawSnapshotSpecData) return;

    const rawEntries: [string, InstrumentV2ForApiInstanceType][] = Object.entries(
      rawSnapshotSpecData.instrumentsKeyBySymbol,
    ).map(([key, instruments]): [string, InstrumentV2ForApiInstanceType] => {
      if (instruments.isSpot) return [key, instruments];
      const lowFreqUpdatedPswap = get(lowFreqUpdatedPswapFamily(key));
      // WS market/pswaps.positionBracket 우선 해석(off-hours 실시간 스위칭) — 심볼 리스트/레버리지 상한도 bracket 스위칭을 따라간다.
      const maxLeverageHardLimit =
        resolvePositionBracket(get(marketPositionBracketFamily(key)), instruments, rawSnapshotSpecData.positionBrackets)
          ?.entries[0]?.maxInitLeverage ?? instruments.maxLeverageHardLimit;
      return [
        key,
        {
          ...instruments,
          maxLeverageHardLimit,
          ...(lowFreqUpdatedPswap
            ? {
                notionalMaxOrderAmount: lowFreqUpdatedPswap.notionalMaxOrderAmount,
                notionalMaxPosition: lowFreqUpdatedPswap.notionalMaxPosition,
              }
            : {}),
        },
      ];
    });

    let entries: typeof rawEntries;
    if (shouldFilterToVaraSymbols) {
      entries = rawEntries
        .filter(([, inst]) => inst.regulationStatus === 'VARA_REGULATED')
        .map(([key, inst]) => [key, { ...inst, displaySymbol: stripVaraPrefix(inst.symbol) }] as const);
    } else {
      entries = rawEntries.filter(([, inst]) => inst.regulationStatus !== 'VARA_REGULATED');
    }

    const spotInstruments = shouldFilterToVaraSymbols
      ? rawSnapshotSpecData.spotInstruments
          .filter((i) => i.regulationStatus === 'VARA_REGULATED')
          .map((i) => ({ ...i, displaySymbol: stripVaraPrefix(i.symbol) }))
      : rawSnapshotSpecData.spotInstruments.filter((i) => i.regulationStatus !== 'VARA_REGULATED');

    return {
      ...rawSnapshotSpecData,
      spotInstruments,
      instrumentsKeyBySymbol: Object.fromEntries(entries),
    };
  });

  const instrumentsKeyBySymbolAtom = atom((get) => get(snapshotSpecAtom)?.instrumentsKeyBySymbol);
  const currenciesKeyByCurrencyAtom = atom((get) => get(snapshotSpecAtom)?.currenciesKeyByCurrency);

  return { snapshotSpecAtom, instrumentsKeyBySymbolAtom, currenciesKeyByCurrencyAtom };
});
snapshotSpecBunja.debugLabel = 'snapshotSpecBunja';
